import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Zap, AlertCircle, QrCode, Building2, Loader2,
  Copy, Clock, CheckCircle2, RefreshCw, Phone, Gamepad2, ShoppingBag, Wallet
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import theme from '../theme.config';
import { formatPrice } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, description: 'GoPay, OVO, Dana, dll' },
  { id: 'va_bni', name: 'VA BNI', icon: Building2, description: 'Transfer via BNI', channel: 'bni' },
  { id: 'va_bri', name: 'VA BRI', icon: Building2, description: 'Transfer via BRI', channel: 'bri' },
  { id: 'va_mandiri', name: 'VA Mandiri', icon: Building2, description: 'Transfer via Mandiri', channel: 'mandiri' },
  { id: 'va_cimb', name: 'VA CIMB', icon: Building2, description: 'Transfer via CIMB', channel: 'cimb' },
];

const BANK_INFO = {
  bni: { name: 'BNI', color: '#f15a22' },
  bri: { name: 'BRI', color: '#00529c' },
  mandiri: { name: 'Mandiri', color: '#003366' },
  cimb: { name: 'CIMB', color: '#ec1c24' },
};

// Default fallback config
const DEFAULT_CFG = {
  type: 'game_id',
  id_label: 'User ID',
  id_placeholder: 'Masukkan User ID',
  id_required: true,
  show_id2: false,
  instruction: 'Masukkan data akun Anda.',
  success_label: 'User ID',
};

// Helper: section heading in Cinzel style
const SectionHeading = ({ num, title }) => (
  <div className="mb-5">
    <div className="section-heading">
      <span style={{ color: 'hsl(var(--primary))' }}>✦ {num}</span>
      <span className="text-foreground/90">/</span>
      <span>{title}</span>
    </div>
    <div className="kala-divider">
      <span className="kala-divider-icon">❖</span>
    </div>
  </div>
);

export default function DigiFlazzProduct() {
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('games');
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const [paymentData, setPaymentData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form');
  const [orderStatus, setOrderStatus] = useState(null);

  useEffect(() => { fetchBrand(); }, [brandSlug]);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  // Poll payment status
  useEffect(() => {
    if (paymentStep !== 'paying' || !paymentData?.orderId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.status === 'paid' || res.data.status === 'completed') {
          setPaymentStep('success');
          return;
        }
        if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          setPaymentStep('failed');
          return;
        }
      } catch {}
      if (active) setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId]);

  // Keep polling on success for SN
  useEffect(() => {
    if (paymentStep !== 'success' || !paymentData?.orderId) return;
    if (orderStatus?.digiflazz_sn && orderStatus?.status === 'completed') return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.digiflazz_sn && res.data.status === 'completed') return;
      } catch {}
      if (active) setTimeout(poll, 5000);
    };
    setTimeout(poll, 3000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId, orderStatus?.digiflazz_sn]);

  const fetchBrand = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biller/catalog/${brandSlug}`);
      setBrand(res.data);
      setProducts(res.data.products || []);
      setCategory(res.data.category || 'games');
      if (res.data.input_config) setCfg(res.data.input_config);
    } catch {
      toast.error('Produk tidak ditemukan');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedSku) return toast.error('Pilih item terlebih dahulu');
    if (!selectedPayment) return toast.error('Pilih metode pembayaran');
    if (cfg.id_required && !userId) return toast.error(`Masukkan ${cfg.id_label}`);
    if (!email) return toast.error('Masukkan email');

    setSubmitting(true);
    try {
      const orderRes = await axios.post(`${API_URL}/api/orders/digiflazz`, {
        brand: brand.brand,
        sku_code: selectedSku.sku,
        product_name: selectedSku.name,
        game_user_id: userId,
        game_server_id: serverId || null,
        email,
        payment_method: selectedPayment.id,
        price: selectedSku.price,
      });
      const orderId = orderRes.data.order.id;

      const isQris = selectedPayment.id === 'qris';
      const payRes = await axios.post(`${API_URL}/api/payment/create`, {
        order_id: orderId,
        amount: selectedSku.price,
        customer_name: email.split('@')[0],
        customer_email: email,
        payment_method: isQris ? 'qris' : 'va',
        va_channel: selectedPayment.channel || 'bni',
        item_name: `${brand?.brand} - ${selectedSku.name}`,
        digiflazz_sku: selectedSku.sku,
        customer_game_id: cfg.show_id2 && serverId ? `${userId}${serverId}` : userId,
      });

      if (payRes.data.success) {
        setPaymentData({ ...payRes.data.data, orderId });
        setPaymentStep('paying');
        toast.success('Payment berhasil dibuat!');
      } else {
        toast.error(payRes.data.message || 'Gagal membuat payment');
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memproses pesanan');
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div
          className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'hsl(var(--primary) / 0.6)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (!brand) return null;

  // ==================== FAILED / CANCELLED ====================
  if (paymentStep === 'failed') {
    return (
      <div className="min-h-screen pt-24 pb-12 stone-texture flex items-center justify-center" style={{ background: 'hsl(var(--background))' }}>
        <div className="max-w-lg mx-auto px-4 text-center stone-card rounded-sm p-8" style={{ border: '1px solid hsl(var(--destructive) / 0.3)' }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{
              background: 'hsl(var(--destructive) / 0.1)',
              border: '1px solid hsl(var(--destructive) / 0.3)',
              boxShadow: '0 0 20px hsl(var(--destructive) / 0.15)'
            }}
          >
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border"
            style={{
              borderColor: 'hsl(var(--destructive) / 0.3)',
              background: 'hsl(var(--destructive) / 0.05)',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
            <span className="text-[10px] text-destructive tracking-widest uppercase font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
              PERSEMBAHAN_GAGAL
            </span>
          </div>
          <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>
            {orderStatus?.status === 'cancelled' ? 'Persembahan Dibatalkan' : 'Persembahan Gagal'}
          </h1>
          <p className="text-muted-foreground text-sm mb-8" style={{ fontFamily: "'Lora', serif" }}>
            {orderStatus?.status === 'cancelled'
              ? 'Ritual persembahan dibatalkan atau waktu QRIS telah kedaluwarsa. Silakan coba kembali.'
              : 'Terjadi kegagalan komunikasi pada gerbang pembayaran. Silakan coba kembali.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              className="border-border text-white hover:bg-white/5 text-xs tracking-wider"
              style={{ fontFamily: "'Cinzel', serif" }}
              onClick={() => { setPaymentStep('form'); setPaymentData(null); setOrderStatus(null); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" /> [ COBA LAGI ]
            </Button>
            <Button
              className="stone-btn font-bold uppercase tracking-widest text-xs text-background"
              style={{
                fontFamily: "'Cinzel', serif",
                background: 'linear-gradient(135deg, #C9A227, #A07B1E)',
                border: 'none',
                color: 'hsl(var(--background))'
              }}
              onClick={() => navigate('/')}
            >
              Kembali ke Beranda
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SUCCESS ====================
  if (paymentStep === 'success') {
    const isVoucher = category === 'voucher';
    const hasSn = orderStatus?.digiflazz_sn;
    const topupDone = orderStatus?.topup_status === 'success';
    const topupFailed = orderStatus?.topup_status === 'failed';
    const isProcessing = !hasSn && !topupFailed;

    return (
      <div className="min-h-screen pt-24 pb-12 stone-texture flex items-center justify-center" style={{ background: 'hsl(var(--background))' }}>
        <div className="max-w-lg mx-auto px-4 text-center stone-card rounded-sm p-8">

          {/* Header - changes based on state */}
          {isProcessing ? (
            <>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'hsl(var(--secondary) / 0.1)',
                  border: '1px solid hsl(var(--secondary) / 0.3)',
                  boxShadow: '0 0 20px hsl(var(--secondary) / 0.15)'
                }}
              >
                <Loader2 className="w-10 h-10 text-secondary animate-spin" />
              </div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                <span className="text-[10px] text-secondary tracking-widest uppercase font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                  PROSES_PERSEMBAHAN
                </span>
              </div>
              <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>
                Memproses Persembahan...
              </h1>
              <p className="text-muted-foreground text-sm mb-4" style={{ fontFamily: "'Lora', serif" }}>
                Pembayaran diterima! Sedang menyalurkan {isVoucher ? 'kode voucher' : category === 'pulsa' ? 'pengiriman pulsa' : 'item top-up'} ke tujuan.
              </p>
              <div className="bg-secondary/5 border border-secondary/20 rounded-sm p-4 mb-6 relative">
                <div className="flex items-center justify-center gap-2 text-secondary text-xs font-bold mb-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                  <AlertCircle className="w-4 h-4" />
                  JANGAN TUTUP HALAMAN INI (TETAP_ONLINE)
                </div>
                <p className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                  {isVoucher ? 'Sistem sedang memproses kode voucher persembahan Anda.' :
                   category === 'pulsa' ? 'Menunggu konfirmasi dari stasiun pulsa.' :
                   'Sistem sedang menyinkronkan data persembahan ke game tujuan.'}
                </p>
              </div>

              {/* Processing animation */}
              <div className="flex justify-center gap-1.5 mb-6">
                {[0,1,2,3,4].map(i => (
                  <div key={i} className="w-2 h-2 rounded-full bg-secondary animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'hsl(var(--primary) / 0.1)',
                  border: '1px solid hsl(var(--primary) / 0.3)',
                  boxShadow: '0 0 20px hsl(var(--primary) / 0.15)'
                }}
              >
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="text-[10px] text-primary tracking-widest uppercase font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                  RITUAL_SELESAI
                </span>
              </div>
              <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>
                {topupFailed ? 'Persembahan Tertunda' : 'Persembahan Selesai!'}
              </h1>
              <p className="text-muted-foreground text-sm mb-6" style={{ fontFamily: "'Lora', serif" }}>
                {topupFailed ? 'Pembayaran terverifikasi namun pengiriman otomatis terganggu. Silakan hubungi Admin.' :
                 `Persembahan digital telah berhasil dikirimkan ke kuil tujuan Anda.`}
              </p>
            </>
          )}

          {/* SN / Voucher Code */}
          {hasSn && (
            <div className="bg-primary/5 border border-primary/30 rounded-sm p-5 mb-6 relative overflow-hidden">
              <span className="absolute top-1 left-1 text-primary/40 text-xs">✦</span>
              <p className="text-[10px] text-primary mb-2 uppercase tracking-widest font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                {isVoucher ? 'KODE VOUCHER DEKRIPSI' : category === 'pulsa' ? 'NOMOR SERI (SN)' : 'TOKEN RITUAL TRANSAKSI'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-white font-bold text-lg break-all">{orderStatus.digiflazz_sn}</span>
                <button onClick={() => copyText(orderStatus.digiflazz_sn)}
                  className="p-2 rounded bg-primary/20 text-primary hover:bg-primary/30 transition-colors flex-shrink-0">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {isVoucher && (
                <p className="text-[10px] text-muted-foreground mt-3" style={{ fontFamily: "'Lora', serif" }}>
                  Gunakan kode di atas pada platform eksternal yang bersangkutan untuk penukaran.
                </p>
              )}
            </div>
          )}

          {/* Top-up failed warning */}
          {topupFailed && (
            <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-4 mb-6 relative">
              <p className="text-red-400 text-xs font-bold font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>GANGGUAN_PENGIRIMAN</p>
              {orderStatus?.topup_error && (
                <p className="text-[10px] text-red-400/70 mt-1" style={{ fontFamily: "'Lora', serif" }}>{orderStatus.topup_error}</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-2" style={{ fontFamily: "'Lora', serif" }}>Silakan hubungi command center admin untuk pemrosesan manual.</p>
            </div>
          )}

          {/* Order details */}
          <div className="bg-black/30 rounded-sm p-6 border border-border mb-6 text-left relative">
            <span className="absolute top-1 left-1 text-primary/40 text-[10px]">✦</span>
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border/40">
              <img src={brand?.image} alt={brand?.brand} className="w-12 h-12 rounded-sm object-cover border border-border" />
              <div>
                <p className="font-bold text-white text-xs uppercase tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>{brand?.brand}</p>
                <p className="text-xs text-primary font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>{selectedSku?.name}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-xs font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                <span className="text-muted-foreground">TARGET ID</span>
                <span className="text-white font-bold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
              </div>
              <div className="flex justify-between text-xs font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                <span className="text-muted-foreground">STATUS PEMBAYARAN</span>
                <span className="text-primary font-bold">SUCCESS</span>
              </div>
              <div className="flex justify-between text-xs font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                <span className="text-muted-foreground">STATUS PENGIRIMAN</span>
                <span className={`font-bold ${topupDone ? 'text-primary' : topupFailed ? 'text-red-500' : 'text-secondary animate-pulse'}`}>
                  {topupDone ? 'SUCCESS' : topupFailed ? 'FAILED' : 'MEMPROSES...'}
                </span>
              </div>
            </div>
            <div className="border-t border-border/40 mt-4 pt-4 flex justify-between items-center">
              <span className="text-muted-foreground text-xs uppercase tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>Total Emas</span>
              <span className="font-mono text-lg font-bold text-primary text-glow-gold" style={{ fontFamily: "'Space Mono', monospace" }}>
                {formatPrice(selectedSku?.price)}
              </span>
            </div>
          </div>

          {/* Buttons - only show when processing is done */}
          {!isProcessing && (
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-border text-white hover:bg-white/5 text-xs tracking-wider"
                style={{ fontFamily: "'Cinzel', serif" }}
                onClick={() => navigate('/track')}
              >
                [ Cek Transaksi ]
              </Button>
              <Button
                className="flex-1 stone-btn font-bold uppercase tracking-widest text-xs"
                style={{
                  fontFamily: "'Cinzel', serif",
                  background: 'linear-gradient(135deg, #C9A227, #A07B1E)',
                  border: 'none',
                  color: 'hsl(var(--background))'
                }}
                onClick={() => navigate('/')}
              >
                Kembali ke Beranda
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== PAYING ====================
  if (paymentStep === 'paying' && paymentData) {
    const isVA = paymentData.payment_method === 'virtual_account';
    return (
      <div className="min-h-screen pt-24 pb-12 stone-texture flex items-center justify-center" style={{ background: 'hsl(var(--background))' }}>
        <div className="max-w-lg mx-auto px-4 w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse"
              style={{
                background: 'hsl(var(--secondary) / 0.1)',
                border: '1px solid hsl(var(--secondary) / 0.3)',
                boxShadow: '0 0 20px hsl(var(--secondary) / 0.15)'
              }}
            >
              <Clock className="w-8 h-8 text-secondary" />
            </div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              <span className="text-[10px] text-secondary tracking-widest uppercase font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>
                PEMBAYARAN_TERTUNDA
              </span>
            </div>
            <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>
              Menunggu Pembayaran
            </h1>
            <p className="text-muted-foreground text-sm" style={{ fontFamily: "'Lora', serif" }}>
              Selesaikan pengiriman emas persembahan sebelum waktu ritual kedaluwarsa (24 jam)
            </p>
          </div>

          <div className="stone-card rounded-sm p-6 mb-6 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border/40">
              <img src={brand.image} alt={brand.brand} className="w-12 h-12 rounded-sm object-cover border border-border" />
              <div>
                <p className="font-bold text-white text-xs uppercase tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>{brand.brand}</p>
                <p className="text-xs text-primary font-mono" style={{ fontFamily: "'Space Mono', monospace" }}>{selectedSku?.name}</p>
              </div>
            </div>
            <div className="text-xs font-mono text-muted-foreground mb-3 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
              {cfg.success_label.toUpperCase()}: <span className="text-white font-bold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
            </div>
            <div className="border-t border-border/40 pt-4 flex justify-between items-center">
              <span className="text-muted-foreground text-xs uppercase tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>Total Persembahan</span>
              <span className="font-mono text-lg font-bold text-primary text-glow-gold" style={{ fontFamily: "'Space Mono', monospace" }}>
                {formatPrice(selectedSku?.price)}
              </span>
            </div>
          </div>

          {isVA ? (
            <div className="stone-card rounded-sm p-6 mb-6 relative overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 rounded-sm font-bold text-xs font-mono text-white"
                  style={{ backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333', fontFamily: "'Space Mono', monospace" }}>
                  {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                </span>
                <span className="text-white text-xs uppercase tracking-widest" style={{ fontFamily: "'Cinzel', serif" }}>
                  Node Virtual Account
                </span>
              </div>
              <div className="bg-black/40 rounded-sm p-4 mb-4 border border-border/50">
                <p className="text-[10px] font-mono text-muted-foreground mb-2" style={{ fontFamily: "'Space Mono', monospace" }}>KODE VIRTUAL ACCOUNT</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lg text-white font-bold tracking-wider" style={{ fontFamily: "'Space Mono', monospace" }}>{paymentData.va_number}</span>
                  <button onClick={() => copyText(paymentData.va_number)} className="p-2 rounded bg-primary/20 text-primary hover:bg-primary/30 transition-colors">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                Transfer ke kode Virtual Account di atas melalui ATM atau mobile banking terminal Anda.
              </p>
            </div>
          ) : (
            <div className="stone-card rounded-sm p-6 mb-6 relative overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <QrCode className="w-6 h-6 text-secondary animate-pulse" />
                <span className="font-bold text-white text-xs uppercase tracking-widest" style={{ fontFamily: "'Cinzel', serif" }}>
                  Pindai QRIS Persembahan
                </span>
              </div>
              <div className="bg-white rounded-sm p-6 mb-4 flex justify-center border border-border/50">
                {paymentData.qr_content ? (
                  <QRCodeCanvas value={paymentData.qr_content} size={220} level="M" marginSize={2} />
                ) : (
                  <div className="w-[220px] h-[220px] flex items-center justify-center">
                    <QrCode className="w-32 h-32 text-gray-300" />
                  </div>
                )}
              </div>
              <p className="text-center text-xs text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                Scan QR code dengan aplikasi e-wallet or mobile banking Anda
              </p>
            </div>
          )}

          <Button
            variant="outline"
            className="w-full border-border text-muted-foreground hover:text-white text-xs uppercase tracking-widest"
            style={{ fontFamily: "'Cinzel', serif" }}
            onClick={() => { setPaymentStep('form'); setPaymentData(null); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Ubah Metode Pembayaran
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-2 font-mono uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
            <Loader2 className="w-3 h-3 animate-spin text-secondary" /> Menghubungi gerbang pembayaran...
          </p>
        </div>
      </div>
    );
  }

  // ==================== FORM ====================

  return (
    <div className="min-h-screen pt-16 stone-texture" style={{ background: 'hsl(var(--background))' }}>
      {/* ── TALL BANNER ── */}
      <div className="relative overflow-hidden" style={{ height: '300px' }}>
        <img
          src={brand.banner_image || brand.image}
          alt={brand.brand}
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.55)' }}
        />
        {/* Gold-to-void gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, hsl(var(--background) / 0.1) 0%, hsl(var(--background) / 0.55) 50%, hsl(var(--background)) 100%)',
          }}
        />
        {/* Subtle gold top line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(to right, transparent, hsl(var(--primary) / 0.4), transparent)' }}
        />

        {/* Banner content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            className="mb-5 text-foreground/60 hover:text-primary text-xs tracking-widest"
            style={{ fontFamily: "'Space Mono', monospace" }}
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            [ KEMBALI ]
          </Button>

          <div className="flex items-center gap-5">
            {/* Product icon with lotus frame */}
            <div
              className="relative flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 12px hsl(var(--primary) / 0.4))' }}
            >
              <img
                src={brand.image}
                alt={brand.brand}
                className="w-16 h-16 md:w-20 md:h-20 rounded-sm object-cover"
                style={{ border: '2px solid hsl(var(--primary) / 0.5)' }}
              />
              <span className="absolute -top-1 -left-1 text-primary/70 text-xs">✦</span>
              <span className="absolute -bottom-1 -right-1 text-secondary/60 text-xs">✦</span>
            </div>
            <div>
              <h1
                className="font-bold text-2xl md:text-4xl text-white uppercase tracking-wider relief-text"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {brand.brand}
              </h1>
              <p
                className="text-primary/70 text-xs uppercase tracking-widest mt-1"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                ✦ {brand.category || category}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT: Temple Offering Chamber ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* ── LEFT COLUMN: Form sections (7/12) ── */}
          <div className="lg:col-span-7 space-y-7">
            {/* Instructions */}
            {cfg.instruction && (
              <div
                className="rounded-sm p-4 relative"
                style={{
                  background: 'hsl(var(--secondary) / 0.06)',
                  border: '1px solid hsl(var(--secondary) / 0.25)',
                }}
              >
                <span className="absolute top-2 left-2 text-secondary/50 text-xs">✦</span>
                <div className="flex items-start gap-3 pl-4">
                  <AlertCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/75" style={{ fontFamily: "'Lora', serif" }}>
                    {cfg.instruction}
                  </p>
                </div>
              </div>
            )}

            {/* ── Section 01: User ID ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading num="01" title={cfg.type === 'voucher_code' ? 'INFORMASI TARGET' : 'DATA TUJUAN'} />
              <div className={`grid grid-cols-1 ${cfg.show_id2 ? 'md:grid-cols-2' : ''} gap-4`}>
                <div className="space-y-2">
                  <Label
                    htmlFor="userId"
                    className="text-xs text-muted-foreground uppercase tracking-widest"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    ▸ {cfg.id_label}
                  </Label>
                  <Input
                    id="userId"
                    placeholder={cfg.id_placeholder}
                    className="text-white rounded-sm focus:ring-1"
                    style={{
                      background: 'hsl(var(--background) / 0.6)',
                      border: '1px solid hsl(var(--border))',
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '13px',
                    }}
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    data-testid="df-user-id"
                    onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                    onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                  />
                </div>
                {cfg.show_id2 && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="serverId"
                      className="text-xs text-muted-foreground uppercase tracking-widest"
                      style={{ fontFamily: "'Space Mono', monospace" }}
                    >
                      ▸ {cfg.id2_label}
                    </Label>
                    <Input
                      id="serverId"
                      placeholder={cfg.id2_placeholder}
                      className="text-white rounded-sm focus:ring-1"
                      style={{
                        background: 'hsl(var(--background) / 0.6)',
                        border: '1px solid hsl(var(--border))',
                        fontFamily: "'Space Mono', monospace",
                        fontSize: '13px',
                      }}
                      value={serverId}
                      onChange={(e) => setServerId(e.target.value)}
                      data-testid="df-server-id"
                      onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                      onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ── Section 02: Item selection ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading num="02" title="PILIH NOMINAL" />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {products.map((p) => (
                  <button
                    key={p.sku}
                    className={`denom-card p-4 rounded-sm text-left flex flex-col justify-between ${selectedSku?.sku === p.sku ? 'selected' : ''}`}
                    onClick={() => setSelectedSku(p)}
                    data-testid={`sku-${p.sku}`}
                  >
                    <div>
                      <p
                        className="font-bold text-white text-xs uppercase tracking-wider line-clamp-2"
                        style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.04em' }}
                      >
                        {p.name}
                      </p>
                    </div>
                    <div className="mt-3 text-right">
                      <p
                        className="text-sm font-bold"
                        style={{ color: 'hsl(var(--primary))', fontFamily: "'Space Mono', monospace" }}
                      >
                        {formatPrice(p.price)}
                      </p>
                      {selectedSku?.sku === p.sku && (
                        <span className="text-primary text-xs">✦</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Section 03: Payment ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading num="03" title="JALUR PEMBAYARAN" />
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.id}
                      className={`payment-card p-3 rounded-sm flex items-center gap-3 ${selectedPayment?.id === m.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPayment(m)}
                      data-testid={`pay-${m.id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'hsl(var(--secondary) / 0.08)',
                          border: '1px solid hsl(var(--secondary) / 0.2)',
                        }}
                      >
                        <Icon className="w-4 h-4" style={{ color: 'hsl(var(--secondary))' }} />
                      </div>
                      <div className="text-left">
                        <p
                          className="font-bold text-white text-xs"
                          style={{ fontFamily: "'Cinzel', serif" }}
                        >
                          {m.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                          {m.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Section 04: Email ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading num="04" title="EMAIL KONFIRMASI" />
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-xs text-muted-foreground uppercase tracking-widest"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  ▸ Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="candi@voltagex.id"
                  className="text-white rounded-sm"
                  style={{
                    background: 'hsl(var(--background) / 0.6)',
                    border: '1px solid hsl(var(--border))',
                    fontFamily: "'Lora', serif",
                  }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="df-email"
                  onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                  onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                />
                <p className="text-[11px] text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                  Bukti transaksi akan dikirim ke alamat email ini
                </p>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Order Summary (5/12) ── */}
          <div className="lg:col-span-5">
            <div
              className="sticky top-24 rounded-sm overflow-hidden"
              style={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--primary) / 0.2)',
                boxShadow: '0 0 40px hsl(var(--primary) / 0.04)',
              }}
            >
              {/* Header */}
              <div
                className="px-6 py-4"
                style={{
                  background: 'hsl(var(--primary) / 0.06)',
                  borderBottom: '1px solid hsl(var(--primary) / 0.15)',
                }}
              >
                <h2
                  className="text-sm text-primary uppercase tracking-widest"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  ✦ Ringkasan Persembahan
                </h2>
              </div>

              <div className="p-6">
                {/* Product preview */}
                <div
                  className="flex items-center gap-3 pb-4 mb-4"
                  style={{ borderBottom: '1px solid hsl(var(--border))' }}
                >
                  <img
                    src={brand.image}
                    alt={brand.brand}
                    className="w-12 h-12 rounded-sm object-cover"
                    style={{ border: '1px solid hsl(var(--border))' }}
                  />
                  <div>
                    <p
                      className="font-bold text-white text-xs uppercase tracking-wide"
                      style={{ fontFamily: "'Cinzel', serif" }}
                    >
                      {brand.brand}
                    </p>
                    {selectedSku && (
                      <p className="text-xs text-primary mt-0.5" style={{ fontFamily: "'Space Mono', monospace" }}>
                        {selectedSku.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-4">
                  {(userId || serverId) && (
                    <div
                      className="rounded-sm p-3"
                      style={{ background: 'hsl(var(--background) / 0.5)', border: '1px solid hsl(var(--border))' }}
                    >
                      {userId && (
                        <p className="text-xs text-foreground/70" style={{ fontFamily: "'Space Mono', monospace" }}>
                          TARGET ID: <span className="text-white font-bold">{userId}</span>
                        </p>
                      )}
                      {serverId && (
                        <p className="text-xs text-foreground/70 mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                          SERVER ID: <span className="text-white font-bold">{serverId}</span>
                        </p>
                      )}
                    </div>
                  )}
                  {selectedPayment && (
                    <div className="flex items-center gap-2 text-xs text-foreground/60">
                      <Wallet className="w-3 h-3" style={{ color: 'hsl(var(--secondary))' }} />
                      <span style={{ fontFamily: "'Space Mono', monospace" }}>
                        {selectedPayment.name.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div
                  className="flex justify-between items-center pt-4 mb-6"
                  style={{ borderTop: '1px solid hsl(var(--border))' }}
                >
                  <span
                    className="text-xs text-muted-foreground uppercase tracking-wider"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    Total Emas
                  </span>
                  <span
                    className="text-xl font-bold text-glow-gold"
                    style={{ color: 'hsl(var(--primary))', fontFamily: "'Space Mono', monospace" }}
                  >
                    {selectedSku ? formatPrice(selectedSku.price) : 'Rp 0'}
                  </span>
                </div>

                {/* CTA */}
                <Button
                  className="w-full stone-btn font-bold uppercase tracking-widest py-6 text-background text-sm"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: (!selectedSku || !selectedPayment)
                      ? 'hsl(var(--muted))'
                      : 'linear-gradient(135deg, #C9A227, #A07B1E)',
                    border: 'none',
                    letterSpacing: '0.06em',
                    cursor: (!selectedSku || !selectedPayment) ? 'not-allowed' : 'pointer',
                    color: (!selectedSku || !selectedPayment) ? 'hsl(var(--muted-foreground))' : 'hsl(var(--background))',
                  }}
                  onClick={handleSubmit}
                  disabled={submitting || !selectedSku || !selectedPayment}
                  data-testid="df-order-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                        style={{ borderColor: 'rgba(0,0,0,0.3)', borderTopColor: 'transparent' }}
                      />
                      Memproses...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      ✦ PERSEMBAHKAN
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
